/**
 * ContractSignPage – E-signature page for candidates
 * Supports typed + drawn signature with full ESIGN/UETA audit trail
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Loader2,
  CheckCircle,
  PenTool,
  Eraser,
  AlertTriangle,
} from "lucide-react";
import SignaturePad from "signature_pad";

export default function ContractSignPage() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  const [typedSignature, setTypedSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [candidateAddress, setCandidateAddress] = useState("");

  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  useEffect(() => {
    if (!canvasRef.current || contract?.status === "signed") return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    sigPadRef.current = new SignaturePad(canvas, {
      backgroundColor: "rgb(255,255,255)",
      penColor: "rgb(0,0,0)",
    });
  }, [contract]);

  const fetchContract = async () => {
    try {
      const res = await fetch(`${API}/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContract(data);
        // Pre-fill address from user profile
        if (user?.assistant_profile?.street_address) {
          setCandidateAddress(user.assistant_profile.street_address);
        }
      } else {
        toast.error("Contract not found");
        navigate("/dashboard");
      }
    } catch {
      toast.error("Failed to load contract");
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const handleSign = async () => {
    if (!typedSignature.trim()) return toast.error("Please type your full legal name");
    if (!consent) return toast.error("You must consent to sign electronically");

    let drawnSig = null;
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      drawnSig = sigPadRef.current.toDataURL("image/png");
    }

    setSigning(true);
    try {
      // Save address to profile if provided
      if (candidateAddress.trim()) {
        await fetch(`${API}/users/profile`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ assistant_profile: { street_address: candidateAddress.trim() } }),
        }).catch(() => {});
      }

      const res = await fetch(`${API}/contracts/${contractId}/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: JSON.stringify({
          typed_signature: typedSignature.trim(),
          drawn_signature: drawnSig,
          consent: true,
          candidate_address: candidateAddress.trim() || null,
        }),
      });

      if (res.ok) {
        toast.success("Contract signed successfully!");
        fetchContract();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to sign contract");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <NavBar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-white/40" />
        </div>
      </div>
    );
  }

  if (!contract) return null;

  const isSigned = contract.status === "signed";
  const isCandidate = user?.user_id === contract.candidate_id;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 pt-24 w-full">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-6 h-6 text-[#FF6B6B]" />
          <h1 className="text-2xl font-bold" data-testid="contract-title">
            {contract.template_title || "Contract"}
          </h1>
        </div>

        {isSigned && (
          <div
            className="flex items-center gap-2 mb-6 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded"
            data-testid="signed-badge"
          >
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">
              Signed on {new Date(contract.signed_at).toLocaleString()} by{" "}
              {contract.typed_signature}
            </span>
          </div>
        )}

        {!isSigned && isCandidate && (
          <div className="flex items-center gap-2 mb-6 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">
              This contract requires your signature
            </span>
          </div>
        )}

        {/* Contract Content */}
        <Card
          className="bg-white border-white/20 p-8 mb-8"
          style={{ background: "#ffffff", backdropFilter: "none", border: "1px solid #e5e5e5" }}
          data-testid="contract-content"
        >
          <div
            className="prose prose-sm max-w-none text-black contract-view"
            dangerouslySetInnerHTML={{ __html: contract.content }}
          />
        </Card>

        {/* Signature Section */}
        {isSigned ? (
          <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="signature-display">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <PenTool className="w-4 h-4 text-[#FF6B6B]" /> Signature
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-white/40 mb-1">Typed Signature</p>
                <p className="text-lg font-serif italic">{contract.typed_signature}</p>
              </div>
              {contract.drawn_signature && (
                <div>
                  <p className="text-xs text-white/40 mb-1">Drawn Signature</p>
                  <img
                    src={contract.drawn_signature}
                    alt="Signature"
                    className="h-20 bg-white rounded p-2"
                  />
                </div>
              )}
              <div className="text-xs text-white/40 border-t border-white/10 pt-3 space-y-1">
                <p>Signed: {new Date(contract.signed_at).toLocaleString()}</p>
                <p>IP: {contract.signer_ip}</p>
                <p>Document Hash: {contract.content_hash_at_signing}</p>
              </div>
            </div>
          </Card>
        ) : isCandidate ? (
          <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="signing-section">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <PenTool className="w-4 h-4 text-[#FF6B6B]" /> Sign This Contract
            </h3>

            <div className="space-y-6">
              {/* Street Address */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Your street address
                </label>
                <Input
                  value={candidateAddress}
                  onChange={(e) => setCandidateAddress(e.target.value)}
                  placeholder="123 Main St, City, State ZIP"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="candidate-address-input"
                />
                <p className="text-xs text-white/40 mt-1">
                  This will be saved to your profile for future contracts.
                </p>
              </div>

              {/* Typed Signature */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Type your full legal name
                </label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Your full legal name"
                  className="bg-white/5 border-white/10 text-white text-lg"
                  data-testid="typed-signature-input"
                />
                {typedSignature && (
                  <p className="mt-2 text-xl font-serif italic text-white/80">
                    {typedSignature}
                  </p>
                )}
              </div>

              {/* Drawn Signature */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium">
                    Draw your signature (optional)
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="text-white/50 hover:text-white text-xs"
                  >
                    <Eraser className="w-3 h-3 mr-1" /> Clear
                  </Button>
                </div>
                <div className="border border-white/10 rounded overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    className="w-full"
                    style={{ height: "120px", touchAction: "none" }}
                    data-testid="signature-canvas"
                  />
                </div>
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/10 rounded">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#FF6B6B] data-[state=checked]:border-[#FF6B6B]"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="text-sm text-white/70 leading-relaxed cursor-pointer">
                  I agree to sign this contract electronically. I understand that
                  my electronic signature has the same legal effect as a
                  handwritten signature under the ESIGN Act and UETA. I consent
                  to conduct this transaction electronically and acknowledge
                  that I have reviewed the contract above.
                </label>
              </div>

              <Button
                onClick={handleSign}
                disabled={signing || !typedSignature.trim() || !consent}
                className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold py-6 text-base"
                data-testid="sign-contract-btn"
              >
                {signing ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <PenTool className="w-5 h-5 mr-2" />
                )}
                Sign Contract
              </Button>
            </div>
          </Card>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
